import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material'
import { scheduleService, ScheduleDetail, EnrollmentDetail } from '../services/scheduleService'

const TestScheduleDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation('test')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEditMode = searchParams.get('edit') === 'true'

  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(isEditMode)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  // 編集用フォーム
  const [editStartDate, setEditStartDate] = useState('')
  const [editEndDate, setEditEndDate] = useState('')

  const loadSchedule = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await scheduleService.getGroupScheduleDetail(id)
      setSchedule(data)

      // 編集フォームの初期値設定
      if (data.startDate) setEditStartDate(data.startDate.split('T')[0])
      if (data.endDate) setEditEndDate(data.endDate.split('T')[0])
    } catch (err) {
      setError(t('schedule.errors.loadFailed'))
      console.error('Schedule loading error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSchedule()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSave = async () => {
    if (!id) return

    try {
      setSaving(true)
      await scheduleService.updateGroupSchedule(id, {
        startDate: editStartDate,
        endDate: editEndDate,
        dueDate: editEndDate,
      })
      await loadSchedule()
      setEditDialogOpen(false)
    } catch (err) {
      setError(t('schedule.errors.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      setSaving(true)
      await scheduleService.deleteGroupSchedule(id)
      navigate('/test-schedules')
    } catch (err) {
      setError(t('schedule.errors.deleteFailed'))
      setDeleteDialogOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip icon={<PlayIcon />} label={t('schedule.status.active')} color="primary" />
      case 'upcoming':
        return (
          <Chip icon={<AccessTimeIcon />} label={t('schedule.status.upcoming')} color="secondary" />
        )
      case 'ended':
        return (
          <Chip icon={<CheckCircleIcon />} label={t('schedule.status.ended')} color="default" />
        )
      case 'inactive':
        return (
          <Chip icon={<WarningIcon />} label={t('schedule.status.inactive')} color="secondary" />
        )
      default:
        return <Chip label={t('schedule.status.notSet')} />
    }
  }

  const getEnrollmentStatusChip = (status: string) => {
    switch (status) {
      case 'ASSIGNED':
        return <Chip size="small" label={t('schedule.enrollmentStatus.ASSIGNED')} />
      case 'IN_PROGRESS':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.IN_PROGRESS')} color="secondary" />
        )
      case 'COMPLETED':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.COMPLETED')} color="primary" />
        )
      case 'OVERDUE':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.OVERDUE')} color="secondary" />
        )
      default:
        return <Chip size="small" label={status} />
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-'
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!schedule) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('schedule.detail.notFound')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/test-schedules')}
          sx={{ mt: 2 }}
        >
          {t('schedule.detail.backToList')}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/test-schedules')}
        >
          {t('schedule.detail.breadcrumb')}
        </Link>
        <Typography color="text.primary">{t('schedule.detail.title')}</Typography>
      </Breadcrumbs>

      {/* ヘッダー */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ScheduleIcon fontSize="large" color="primary" />
            {schedule.test.title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label={schedule.test.complianceRule.category} size="small" />
            {getStatusChip(schedule.status)}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/test-schedules')}>
            {t('schedule.detail.backToList')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AssessmentIcon />}
            onClick={() => navigate(`/test-schedules/${id}/report`)}
          >
            {t('schedule.actions.viewReport')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
          >
            {t('schedule.actions.edit')}
          </Button>
          <Button
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { borderColor: '#EF4444', color: '#EF4444' },
            }}
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            {t('schedule.actions.delete')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 概要カード */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        {/* スケジュール情報 */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ScheduleIcon color="primary" />
              {t('schedule.detail.scheduleInfo')}
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">
                  {t('schedule.detail.startDateLabel')}
                </Typography>
                <Typography fontWeight="bold">{formatDate(schedule.startDate)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{t('schedule.detail.endDateLabel')}</Typography>
                <Typography fontWeight="bold">{formatDate(schedule.endDate)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">
                  {t('schedule.detail.createdAtLabel')}
                </Typography>
                <Typography>{formatDateTime(schedule.assignedAt)}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 対象グループ */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <GroupIcon color="primary" />
              {t('schedule.detail.targetGroup')}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {schedule.userGroup.name}
            </Typography>
            {schedule.userGroup.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {schedule.userGroup.description}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('schedule.detail.memberCount', { count: schedule.progressInfo.totalCount })}
            </Typography>
          </CardContent>
        </Card>

        {/* 進捗状況 */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AssignmentIcon color="primary" />
              {t('schedule.detail.progressStatus')}
            </Typography>
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="h2" fontWeight="bold" color="primary">
                {schedule.progressInfo.progressRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.detail.completedCount', {
                  completed: schedule.progressInfo.completedCount,
                  total: schedule.progressInfo.totalCount,
                })}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={schedule.progressInfo.progressRate}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <Chip
                size="small"
                label={`${t('schedule.progressInfo.notStarted')}: ${schedule.progressInfo.statusBreakdown.ASSIGNED}`}
              />
              <Chip
                size="small"
                label={`${t('schedule.progressInfo.inProgress')}: ${schedule.progressInfo.statusBreakdown.IN_PROGRESS}`}
                color="secondary"
              />
              <Chip
                size="small"
                label={`${t('schedule.progressInfo.completedStatus')}: ${schedule.progressInfo.statusBreakdown.COMPLETED}`}
                color="primary"
              />
              <Chip
                size="small"
                label={`${t('schedule.progressInfo.overdue')}: ${schedule.progressInfo.statusBreakdown.OVERDUE}`}
                color="secondary"
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* 受講者一覧 */}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <PersonIcon color="primary" />
          {t('schedule.detail.enrolleeList')}
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>{t('schedule.enrollee.name')}</TableCell>
                <TableCell>{t('schedule.enrollee.email')}</TableCell>
                <TableCell>{t('schedule.enrollee.department')}</TableCell>
                <TableCell>{t('schedule.enrollee.status')}</TableCell>
                <TableCell>{t('schedule.enrollee.startedAt')}</TableCell>
                <TableCell>{t('schedule.enrollee.lastAccess')}</TableCell>
                <TableCell>{t('schedule.enrollee.result')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedule.enrollments.map((enrollment: EnrollmentDetail) => (
                <TableRow key={enrollment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {enrollment.user.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{enrollment.user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{enrollment.user.department || '-'}</Typography>
                  </TableCell>
                  <TableCell>{getEnrollmentStatusChip(enrollment.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(enrollment.startedAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(enrollment.lastAccessAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {enrollment.latestResult ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {enrollment.latestResult.passed ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          <CancelIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        )}
                        <Typography variant="body2" fontWeight="bold">
                          {t('schedule.enrollee.scorePoints', {
                            score: enrollment.latestResult.score,
                          })}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            enrollment.latestResult.passed ? t('result.passed') : t('result.failed')
                          }
                          color={enrollment.latestResult.passed ? 'primary' : 'default'}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.enrollee.notTaken')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('schedule.editDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel htmlFor="edit-start-date">{t('schedule.form.startDate')}</InputLabel>
              <OutlinedInput
                id="edit-start-date"
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                label={t('schedule.form.startDate')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel htmlFor="edit-end-date">{t('schedule.form.endDate')}</InputLabel>
              <OutlinedInput
                id="edit-end-date"
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                label={t('schedule.form.endDate')}
                inputProps={{ min: editStartDate }}
              />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            {t('schedule.editDialog.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {t('schedule.editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('schedule.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('schedule.deleteDialog.confirmMessage')}</Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            {t('schedule.deleteDialog.warningMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            {t('schedule.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {t('schedule.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TestScheduleDetailPage
